/**
 * Route configuration for the standard Prior Authorization (PA) workflow.
 *
 * Defines nested routes for worklists, single-order views, patients,
 * eligibility, analytics, configurations, and settings.
 * @module paRouteConfig
 */

import { SettingsPage } from "@risalabs_frontend_org/auth-wrapper";
import { Navigate } from "react-router-dom";
import ErrorPage from "../components/errorPage/errorPage";
import OrgOnlyRoute from "../components/orgOnlyRoute/orgOnlyRoute";
import ProtectedAdminRoute from "../components/protectedAdminRoute/protectedAdminRoute";
import { BusniessOfficeNotesContextProvider } from "../context/busniessOfficeNotesContext";
import { DataAccumulatorForSingleOrderProvider } from "../context/dataAccumulatorForSingleOrder";
import { InitialDataOfSingleMedicalOrderProvider } from "../context/initialDataOfSingleMedicalOrder";
import { PharmaFormFieldsProvider } from "../context/pharmaFormFieldsContext";
import { exceptionFlowForUrologyOrgs } from "../enums/organizationTypes";
import AnalyticsNew from "../pages/analyticsNew/analyticsNew";
import AuthorisationDetails from "../pages/authorisationDetails/authorisationDetails";
import Claims from "../pages/claims/claims";
import ClientConfiguration from "../pages/clientConfiguration/clientConfiguration";
import Configurations from "../pages/configurations/configurations";
import AgenticAuthOnFileConfiguration from "../pages/configurations/pages/agenticAuthOnFileConfiguration/agenticAuthOnFileConfiguration";
import AuthLetterAgentConfiguration from "../pages/configurations/pages/authLetterAgentConfiguration/authLetterAgentConfiguration";
import AuthorizationLetterConfiguration from "../pages/configurations/pages/authorizationLetterConfiguration/authorizationLetterConfiguration";
import DataMigration from "../pages/configurations/pages/dataMigration/dataMigration";
import DocumentsToAttach from "../pages/configurations/pages/documentsConfiguration/documentsToAttach";
import DxCodeConfig from "../pages/configurations/pages/dxCodeConfig/dxCodeConfig";
import EmailPasswordConfiguration from "../pages/configurations/pages/emailPasswordConfiguration/emailPasswordConfiguration";
import FormConfig from "../pages/configurations/pages/formsConfiguration/formConfig";
import FormConfigurationLayout from "../pages/configurations/pages/formsConfiguration/formConfigurationLayout";
import SingleFormConfiguration from "../pages/configurations/pages/formsConfiguration/singleFormConfiguration/singleFormConfiguration";
import GiRpaConfiguration from "../pages/configurations/pages/giRpaConfiguration/giRpaConfiguration";
import HealthFacilityConfiguration from "../pages/configurations/pages/healthFacilityConfiguration/healthFacilityConfiguration";
import HistoricNarConfigLayout from "../pages/configurations/pages/historicNarConfig/historicNarConfigLayout";
import MedicineNameConfiguration from "../pages/configurations/pages/medicineNameConfiguration/medicineNameConfiguration";
import NarAgentGridLayout from "../pages/configurations/pages/narAgentGrid/narAgentGridLayout";
import NarConfigurationLayout from "../pages/configurations/pages/narConfiguration/narConfigurationLayout";
import OptionsConfiguration from "../pages/configurations/pages/optionsConfiguration/optionsConfiguration";
import OrderAssignmentConfiguration from "../pages/configurations/pages/orderAssignmentConfiguration/orderAssignmentConfiguration";
import PaFormConfiguration from "../pages/configurations/pages/paFormConfiguration/paFormConfiguration";
import ClinicalQuestionnaire from "../pages/configurations/pages/pharmaPaConfiguration/pages/clinicalQuestionnaire/clinicalQuestionnaire";
import DrugsConfiguration from "../pages/configurations/pages/pharmaPaConfiguration/pages/drugConfig/drugsConfig";
import PayerConfiguration from "../pages/configurations/pages/pharmaPaConfiguration/pages/payerConfiguration/payerConfiuratiom";
import PbmConfiguration from "../pages/configurations/pages/pharmaPaConfiguration/pages/pbmConfiguration/pbmConfiguration";
import ProviderList from "../pages/configurations/pages/pharmaPaConfiguration/pages/providerList/providerList";
import SendToPlanConfig from "../pages/configurations/pages/pharmaPaConfiguration/pages/sendToPlanConfig/sendToPlanConfig";
import PharmaPaConfigurationLayout from "../pages/configurations/pages/pharmaPaConfiguration/pharmaPaConfigurationLayout";
import PrescriptionParsingConfiguration from "../pages/configurations/pages/prescriptionParsingConfiguration/prescriptionParsingConfiguration";
import RpaConfiguration from "../pages/configurations/pages/rpaConfiguration/rpaConfiguration";
import CoverageInfo from "../pages/coverageInfo/coverageInfo";
import CredentialsConfig from "../pages/credentialsConfigration/credentialsConfig";
import ExpandableOrderAuthTestPage from "../pages/dev/expandableOrderAuthTest/expandableOrderAuthTestPage";
import DraftSubmission from "../pages/draftSubsmission/draftSubmission";
import EligibilityCheck from "../pages/eligibilityCheck/eligibilityCheck";
import FaxFormQuestionnaireLayout from "../pages/faxFormQuestionnaire/faxFormQuestionnaireLayout";
import FaxLetterDetails from "../pages/faxLetterDetails/faxLetterDetails";
import FinalDocuments from "../pages/finalDocuments/finalDocuments";
import MedicalNecessityAnalysis from "../pages/medicalNecessityAnalysis/medicalNecessityAnalysis";
import MedicalPaFormLayout from "../pages/medicalPaForm/medicalPaFormLayout";
import MedicalPaLoaderScreen from "../pages/medicalPaLoaderScreen/medicalPaLoaderScreen";
import PatientComponent from "../pages/patients/patientComponent";
import PatientTable from "../pages/patients/patientTable/patientTable";
import SinglePatientComponent from "../pages/patients/single-patient/singlePatient";
import PortalQuestionnaireLayout from "../pages/portalQuestionnaire/portalQuestionnaireLayout";
import ProcedurePaFormLayout from "../pages/procedurePaForm/ProcedurePaFormLayout";
import ProcedureQuestionaireLayout from "../pages/procedureQuestionaire/procedureQuestionaireLayout";
import BusniessOffice from "../pages/singleOrderLayout/components/busniessOffice/busniessOffice";
import SingleOrderLayout from "../pages/singleOrderLayout/singleOrderLayout";
import SubmissionStatus from "../pages/submissionStatus/submissionStatus";
import AllOpenOrders from "../pages/worklists/allOpenOrders/allOpenOrders";
import FinalWorklist from "../pages/worklists/finalWorklist/finalWorklist";
import FinalWorklistUpload from "../pages/worklists/finalWorklistUpload/finalWorklistUpload";
import GenericAuthStatusOrders from "../pages/worklists/genericAuthStatusLayout/genericAuthStatusOrders";
import MedicalPaOrdersAlgolia from "../pages/worklists/medicalPaOrdersAlgolia/medicalPaOrders";
import MyPaOrders from "../pages/worklists/myPaOrders/myPaOrders";
import QuickReviewOrders from "../pages/worklists/quickReviewOrders/quickReviewOrders";
import ReviewPendingOrdersLayout from "../pages/worklists/reviewPendingOrders/reviewPendingOrdersLayout";
import AuthLetters from "../pages/worklists/singleOrder/authLetters/authLetters";
import AuthNotes from "../pages/worklists/singleOrder/authNotes/authNotes";
import CandidUpload from "../pages/worklists/singleOrder/candidUpload/candidUpload";
import CptCodes from "../pages/worklists/singleOrder/cptCodes/cptCodes";
import Documents from "../pages/worklists/singleOrder/documents/documents";
import QueryComments from "../pages/worklists/singleOrder/queryComments/queryComments";
import Tasks from "../pages/worklists/singleOrder/tasks/tasks";
import TreatmentPlan from "../pages/worklists/singleOrder/treatmentPlan/treatmentPlan";
import VisitData from "../pages/worklists/singleOrder/visitData/visitData";
import Worklists from "../pages/worklists/worklists";
import { WorklistTabLayout } from "../pages/worklists/WorklistTabLayout";
import { isProduction as isProductionEnv } from "../utils/runtimeEnv";

const isProduction = isProductionEnv();

/**
 * Worklist routes restricted to orgs in `exceptionFlowForUrologyOrgs`.
 *
 * @returns Route objects for urology-only worklists (Orders to Process, Quick Review)
 */
const getUrologyExceptionFlowRoutes = () => [
  {
    path: "orders-to-process",
    element: (
      <OrgOnlyRoute orgIds={[...exceptionFlowForUrologyOrgs]}>
        <MedicalPaOrdersAlgolia />
      </OrgOnlyRoute>
    ),
  },
  {
    path: "quick-review",
    element: (
      <OrgOnlyRoute orgIds={[...exceptionFlowForUrologyOrgs]}>
        <QuickReviewOrders />
      </OrgOnlyRoute>
    ),
  },
];

/** React Router route objects for the PA dashboard. Consumed by the top-level router. */
export const PaRouteConfig = [
  { index: true, element: <Worklists /> },
  {
    path: "worklists",
    // A single accumulator provider is hoisted here so its per-order cache
    // survives navigation between the single-order, coverage (evbv), and
    // medical-pa-form child routes. React Router keeps this parent element
    // mounted while swapping the child outlet. Worklist table routes reset
    // the cache; order-detail navigation reuses it within the current visit.
    element: (
      <DataAccumulatorForSingleOrderProvider>
        <Worklists />
      </DataAccumulatorForSingleOrderProvider>
    ),
    children: [
      { index: true, element: <Navigate to="medical-pa-orders" replace /> },
      {
        element: <WorklistTabLayout />,
        children: [
          { path: "medical-pa-orders", element: <MedicalPaOrdersAlgolia /> },
          { path: "all-open-orders", element: <AllOpenOrders /> },
          { path: "my-pa-orders", element: <MyPaOrders /> },
          ...getUrologyExceptionFlowRoutes(),
          {
            path: "review-pending/:authStatus?",
            element: <ReviewPendingOrdersLayout />,
          },
          { path: "final-worklist", element: <FinalWorklist /> },
          { path: "final-worklist-upload", element: <FinalWorklistUpload /> },
          { path: ":tabId", element: <GenericAuthStatusOrders /> },
        ],
      },
      ...(!isProduction
        ? [
            {
              path: "expandable-order-auth-test",
              element: <ExpandableOrderAuthTestPage />,
            },
          ]
        : []),
      {
        path: "authorisation-details/:authId",
        element: <AuthorisationDetails />,
      },
      {
        path: "authorisation-details/:authId/fax-letter/:jCodeId/:documentId",
        element: <FaxLetterDetails />,
      },
      {
        path: "singleOrder/:orderId?",
        element: (
          <BusniessOfficeNotesContextProvider>
            <InitialDataOfSingleMedicalOrderProvider>
              <SingleOrderLayout />
            </InitialDataOfSingleMedicalOrderProvider>
          </BusniessOfficeNotesContextProvider>
        ),
        children: [
          { index: true, element: <Navigate to="cptCodes" replace /> },
          { path: "cptCodes", element: <CptCodes /> },
          { path: "comments", element: <BusniessOffice /> },
          { path: "treatmentPlan", element: <TreatmentPlan /> },
          { path: "visitData", element: <VisitData /> },
          { path: "documents", element: <Documents /> },
          { path: "tasks", element: <Tasks /> },
          { path: "queryComments", element: <QueryComments /> },
          { path: "authLetters", element: <AuthLetters /> },
          { path: "authNotes", element: <AuthNotes /> },
          { path: "candidUpdate", element: <CandidUpload /> },
        ],
      },
      {
        path: "medical-necessity-analysis/:orderId",
        element: (
          <DataAccumulatorForSingleOrderProvider>
            <MedicalNecessityAnalysis />
          </DataAccumulatorForSingleOrderProvider>
        ),
      },
      {
        path: "evbv/:id?",
        element: (
          <BusniessOfficeNotesContextProvider>
            <InitialDataOfSingleMedicalOrderProvider>
              <CoverageInfo />
            </InitialDataOfSingleMedicalOrderProvider>
          </BusniessOfficeNotesContextProvider>
        ),
      },
      {
        path: "final-document/:id?",
        element: (
          <DataAccumulatorForSingleOrderProvider>
            <FinalDocuments />
          </DataAccumulatorForSingleOrderProvider>
        ),
      },
      {
        path: "procedure-pa-form/:id?",
        element: <ProcedurePaFormLayout />,
      },
      {
        path: "submission-status/:id",
        element: <SubmissionStatus />,
      },
      {
        path: "procedure-questionaire/:id",
        element: (
          <DataAccumulatorForSingleOrderProvider>
            <ProcedureQuestionaireLayout />
          </DataAccumulatorForSingleOrderProvider>
        ),
      },
      {
        path: "fax-form-questionnaire/:id",
        element: <FaxFormQuestionnaireLayout />,
      },
      {
        path: "medical-pa-form/:id",
        element: (
          <PharmaFormFieldsProvider>
            <MedicalPaFormLayout />
          </PharmaFormFieldsProvider>
        ),
      },
      {
        path: "medical-pa-loader-screen/:id",
        element: <MedicalPaLoaderScreen />,
      },
      {
        path: "draft-submission/:id",
        element: <DraftSubmission />,
      },
      {
        path: "portal-questionnaire/:id",
        element: (
          <DataAccumulatorForSingleOrderProvider>
            <PortalQuestionnaireLayout />
          </DataAccumulatorForSingleOrderProvider>
        ),
      },
    ],
  },
  {
    path: "patients",
    element: <PatientComponent />,
    children: [
      { index: true, element: <PatientTable /> },
      {
        path: "singlePatient/:patientId?",
        element: <SinglePatientComponent />,
      },
    ],
  },
  {
    path: "eligibilty-check",
    element: <EligibilityCheck />,
  },
  {
    path: "claims-check",
    element: <Claims />,
  },
  {
    path: "external-analytics",
    element: <AnalyticsNew />,
  },

  {
    path: "configurations",
    element: (
      <ProtectedAdminRoute>
        <Configurations />
      </ProtectedAdminRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="agentic-auth-on-file-configuration" replace />,
      },
      { path: "documents-configuration", element: <DocumentsToAttach /> },
      { path: "rpa-configuration", element: <RpaConfiguration /> },
      {
        path: "form-configuration",
        element: <FormConfigurationLayout />,
        children: [
          { index: true, element: <Navigate to="form-config" replace /> },
          { path: "form-config", element: <FormConfig /> },
          {
            path: "form-config/:id",
            element: <SingleFormConfiguration />,
          },
        ],
      },
      {
        path: "pa-form-configuration",
        element: <PaFormConfiguration />,
      },
      {
        path: "medicine-name-configuration",
        element: <MedicineNameConfiguration />,
      },
      {
        path: "prescription-parsing-configuration",
        element: <PrescriptionParsingConfiguration />,
      },
      {
        path: "pharma-pa-configuration",
        element: <PharmaPaConfigurationLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="send-to-plan-config" replace />,
          },
          { path: "send-to-plan-config", element: <SendToPlanConfig /> },
          { path: "pbm-configurations", element: <PbmConfiguration /> },
          { path: "drugs-configuration", element: <DrugsConfiguration /> },
          { path: "clinical-questionaire", element: <ClinicalQuestionnaire /> },
        ],
      },
      { path: "credentials-configuration", element: <CredentialsConfig /> },
      { path: "client-configuration", element: <ClientConfiguration /> },
      { path: "provider-list", element: <ProviderList /> },
      { path: "payer-config", element: <PayerConfiguration /> },
      { path: "nar-agent-grid", element: <NarAgentGridLayout /> },
      { path: "historic-nar-config", element: <HistoricNarConfigLayout /> },
      { path: "nar-configuration", element: <NarConfigurationLayout /> },
      {
        path: "health-facility",
        element: <HealthFacilityConfiguration />,
      },
      { path: "gi-configuration", element: <GiRpaConfiguration /> },
      {
        path: "email-password-configuration",
        element: <EmailPasswordConfiguration />,
      },
      {
        path: "dx-code-config",
        element: <DxCodeConfig />,
      },
      {
        path: "options-configuration",
        element: <OptionsConfiguration />,
      },
      {
        path: "authorization-letter-configuration",
        element: <AuthorizationLetterConfiguration />,
      },
      {
        path: "agentic-auth-on-file-configuration",
        element: <AgenticAuthOnFileConfiguration />,
      },
      {
        path: "auto-assignment-configuration",
        element: <OrderAssignmentConfiguration />,
      },
      {
        path: "auth-letter-agent-configuration",
        element: <AuthLetterAgentConfiguration />,
      },
      ...(isProduction
        ? [{ path: "data-migration", element: <DataMigration /> }]
        : []),
    ],
  },
  {
    path: "settings/*",
    element: (
      <SettingsPage
        enabledPages={[
          "profile",
          "security",
          "members",
          "support",
          "superadmin",
        ]}
        roleOptions={[
          { value: "admin_med_onc", label: "Admin" },
          { value: "standard_med_onc", label: "Member" },
        ]}
      />
    ),
  },
  {
    path: "configurations",
    element: (
      <ProtectedAdminRoute>
        <Configurations />
      </ProtectedAdminRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="agentic-auth-on-file-configuration" replace />,
      },
      { path: "documents-configuration", element: <DocumentsToAttach /> },
      {
        path: "form-configuration",
        element: <FormConfigurationLayout />,
        children: [
          { index: true, element: <Navigate to="form-config" replace /> },
          { path: "form-config", element: <FormConfig /> },
          {
            path: "form-config/:id",
            element: <SingleFormConfiguration />,
          },
        ],
      },
      {
        path: "pa-form-configuration",
        element: <PaFormConfiguration />,
      },
      {
        path: "medicine-name-configuration",
        element: <MedicineNameConfiguration />,
      },
      {
        path: "prescription-parsing-configuration",
        element: <PrescriptionParsingConfiguration />,
      },
      {
        path: "pharma-pa-configuration",
        element: <PharmaPaConfigurationLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="send-to-plan-config" replace />,
          },
          { path: "send-to-plan-config", element: <SendToPlanConfig /> },
          { path: "pbm-configurations", element: <PbmConfiguration /> },
        ],
      },
      {
        path: "health-facility",
        element: <HealthFacilityConfiguration />,
      },
      {
        path: "options-configuration",
        element: <OptionsConfiguration />,
      },
      {
        path: "email-password-configuration",
        element: <EmailPasswordConfiguration />,
      },
      {
        path: "dx-code-config",
        element: <DxCodeConfig />,
      },
      {
        path: "authorization-letter-configuration",
        element: <AuthorizationLetterConfiguration />,
      },
      {
        path: "agentic-auth-on-file-configuration",
        element: <AgenticAuthOnFileConfiguration />,
      },
      {
        path: "auto-assignment-configuration",
        element: <OrderAssignmentConfiguration />,
      },
      ...(isProduction
        ? [{ path: "data-migration", element: <DataMigration /> }]
        : []),
    ],
  },
  {
    path: "*",
    element: <ErrorPage />,
  },
];
